import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Grid3x3, Plus, RefreshCw, Loader, Settings, Search, X, Loader2, Printer, Copy, Expand, Undo2, ExternalLink, Stethoscope, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { stcBinService } from '../../../../services/stcBinService';
import { RackSummary, BinLocation, FilterState, FilterOptions, RackStructure, BinLocationGroup, BulkUndoData } from './types';
import { LabelPrintModal, LabelPrintItem } from '../../../common/LabelPrintModal';
import {
    RackGridCore,
    DetailPanel,
    useBinReferenceData,
    useBinContents,
    CellColor,
    GridDensity,
    LegendItem,
    colorForCategoryIndex,
    colorForFillRate,
    CONTENT_EMPTY,
    CONTENT_OCCUPIED,
    FILL_UNKNOWN,
    compareSegments
} from '../../../common/rack-grid';
import { CreateRackDialog, CreateRackMode, RackDialogPrefill } from './CreateRackDialog';
import { BulkUpdateModal } from './BulkUpdateModal';
import { FilterPanel } from './FilterPanel';
import { HealthCheckPanel } from './HealthCheckPanel';
import { BackButton } from '../../../common/BackButton';

/** Farge-lag i griden: standard (konfig), innhold (tom/opptatt), eller kategorisk dimensjon */
type ColorMode = 'none' | 'content' | 'zone' | 'binType' | 'pickPriority' | 'fillRate';

/**
 * Reolbygger / Warehouse Builder Application (FR-101 - FR-108)
 *
 * Optimized for large screens to display maximum locations
 * Layout: 2-column layout (filter panel + grid view)
 * Bulk update operations now in modal dialog (BulkUpdateModal)
 * Grid-view is the dominant element that controls page height
 */

interface RackBuilderAppProps {
  onBack?: () => void;
}

export const RackBuilderApp: React.FC<RackBuilderAppProps> = ({ onBack }) => {
    const { user } = useAuth();
    const { currentWarehouse } = useWarehouse();
    const { t } = useTranslation();
    const [racks, setRacks] = useState<RackSummary[]>([]);
    const [selectedRackNumbers, setSelectedRackNumbers] = useState<string[]>([]);
    const [rackStructures, setRackStructures] = useState<Map<string, RackStructure>>(new Map());
    const [allLocations, setAllLocations] = useState<BinLocation[]>([]);
    const [filteredLocations, setFilteredLocations] = useState<BinLocation[]>([]);
    const [selectedBinCodes, setSelectedBinCodes] = useState<string[]>([]);
    const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filterError, setFilterError] = useState<boolean>(false);
    const [rackDialog, setRackDialog] = useState<{ mode: CreateRackMode; prefill?: RackDialogPrefill } | null>(null);
    const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);

    // Kompakt reolvelger (chips-rad) + kollapsbar filterkolonne + tverrfilter
    const [rackSearch, setRackSearch] = useState('');
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    /** Treff per reol i hele lageret når filter er aktivt (null = ingen aktive filtre) */
    const [rackHitCounts, setRackHitCounts] = useState<Map<string, number> | null>(null);

    // RackGridV2: farge-lag, tetthet, søk og detaljpanel
    const [colorMode, setColorMode] = useState<ColorMode>('none');
    const [density, setDensity] = useState<GridDensity>('L');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchMatchIndex, setSearchMatchIndex] = useState(0);
    const [scrollTarget, setScrollTarget] = useState<string | null>(null);
    const [detailGroup, setDetailGroup] = useState<BinLocationGroup | null>(null);

    // Fase 3: helsesjekk-panel
    const [showHealthCheck, setShowHealthCheck] = useState(false);

    // Fase 2: etikettutskrift, angre masseendring, deep-link
    const [showLabelModal, setShowLabelModal] = useState(false);
    const [lastBulkUndo, setLastBulkUndo] = useState<BulkUndoData | null>(null);
    const [isUndoing, setIsUndoing] = useState(false);
    const [bulkNotice, setBulkNotice] = useState<{ type: 'success' | 'warning'; text: string } | null>(null);
    // Deep-link fra Lokasjon HeatMap: ?rack=A&bin=A1307 — bin scrolles til når reolen er lastet
    const pendingDeepLinkBin = useRef<string | null>(null);

    // Default filter state with extended properties
    const [filterState, setFilterState] = useState<FilterState>({
        binSizeNo: [],
        binTypeNo: [],
        resourceNo: [],
        stcZoneID: [],
        binGrpNo: [],
        stcBinCatNo: [],
        pickPriorityNo: [],
        pickableYesNo: 'all',
        itemNo: 'all',
        // Extended filter state
        putAwayPriorityNo: [],
        reservationAllowed: 'all',
        autoReplenishment: 'all',
        negativeReservationAllowed: 'all',
        sharedBinYesNo: 'all',
        sharedBinPreferEmptyBin: 'all',
        replenishMinLevelFrom: 0,
        replenishMinLevelTo: 9999,
        replenishMaxLevelFrom: 0,
        replenishMaxLevelTo: 9999,
        customSortIDFrom: 0,
        customSortIDTo: 9999,
        sharedBinMaxQuantityFrom: 0,
        sharedBinMaxQuantityTo: 9999
    });

    const companyNo = user?.companyNo || '';
    const warehouseID = currentWarehouse || '1';

    // Stabil array-referanse så RackGrid kan memoisere Set-oppslagene sine
    const filteredBinCodes = useMemo(
        () => filteredLocations.map(l => l.binCode),
        [filteredLocations]
    );

    /** Minst ett filter aktivt? Uten aktive filtre kalles ikke filter-API-et i det hele tatt. */
    const hasActiveFilters = useMemo(() => (
        filterState.binSizeNo.length > 0 ||
        filterState.binTypeNo.length > 0 ||
        filterState.resourceNo.length > 0 ||
        filterState.stcZoneID.length > 0 ||
        filterState.binGrpNo.length > 0 ||
        filterState.stcBinCatNo.length > 0 ||
        filterState.pickPriorityNo.length > 0 ||
        filterState.putAwayPriorityNo.length > 0 ||
        filterState.pickableYesNo !== 'all' ||
        filterState.itemNo !== 'all' ||
        filterState.reservationAllowed !== 'all' ||
        filterState.autoReplenishment !== 'all' ||
        filterState.negativeReservationAllowed !== 'all' ||
        filterState.sharedBinYesNo !== 'all' ||
        filterState.sharedBinPreferEmptyBin !== 'all' ||
        filterState.replenishMinLevelFrom > 0 || filterState.replenishMinLevelTo < 9999 ||
        filterState.replenishMaxLevelFrom > 0 || filterState.replenishMaxLevelTo < 9999 ||
        filterState.customSortIDFrom > 0 || filterState.customSortIDTo < 9999 ||
        filterState.sharedBinMaxQuantityFrom > 0 || filterState.sharedBinMaxQuantityTo < 9999
    ), [filterState]);

    /** Aktive filtre som chips i verktøylinjen — X nullstiller den ene kategorien */
    const activeFilterChips = useMemo(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        const yes = t('manager.modules.rackBuilder.common.yes', {}, 'Ja');
        const no = t('manager.modules.rackBuilder.common.no', {}, 'Nei');
        const add = (key: string, label: string, patch: Partial<FilterState>) => {
            chips.push({ key, label, onClear: () => setFilterState(prev => ({ ...prev, ...patch })) });
        };
        const multi = (key: string, labelKey: string, fallback: string, values: (number | string)[], patch: Partial<FilterState>) => {
            if (values.length > 0) add(key, `${t(labelKey, {}, fallback)} (${values.length})`, patch);
        };
        const tri = (key: string, labelKey: string, fallback: string, value: string, patch: Partial<FilterState>) => {
            if (value !== 'all') add(key, `${t(labelKey, {}, fallback)}: ${value === 'yes' ? yes : no}`, patch);
        };

        multi('binSize', 'manager.modules.rackBuilder.fields.binSize', 'Bin Storrelse', filterState.binSizeNo, { binSizeNo: [] });
        multi('binType', 'manager.modules.rackBuilder.fields.binType', 'Bin Type', filterState.binTypeNo, { binTypeNo: [] });
        multi('resource', 'manager.modules.rackBuilder.fields.resource', 'Ressurs', filterState.resourceNo, { resourceNo: [] });
        multi('zone', 'warehouse.general.zone', 'Sone', filterState.stcZoneID, { stcZoneID: [] });
        multi('binGroup', 'manager.modules.rackBuilder.fields.binGroup', 'Bin Gruppe', filterState.binGrpNo, { binGrpNo: [] });
        multi('binCategory', 'manager.modules.rackBuilder.fields.binCategory', 'Lokasjonskategori', filterState.stcBinCatNo, { stcBinCatNo: [] });
        multi('pickPriority', 'manager.modules.rackBuilder.fields.pickPriority', 'Plukk Prioritet', filterState.pickPriorityNo, { pickPriorityNo: [] });
        multi('putAwayPriority', 'manager.modules.rackBuilder.fields.putAwayPriority', 'Put-away Prioritet', filterState.putAwayPriorityNo, { putAwayPriorityNo: [] });
        tri('pickable', 'manager.modules.rackBuilder.fields.pickable', 'Plukkbar', filterState.pickableYesNo, { pickableYesNo: 'all' });
        tri('reservation', 'manager.modules.rackBuilder.fields.reservation', 'Reservasjon', filterState.reservationAllowed, { reservationAllowed: 'all' });
        tri('autoReplenishment', 'manager.modules.rackBuilder.fields.autoReplenishment', 'Auto-pafylling', filterState.autoReplenishment, { autoReplenishment: 'all' });
        tri('negativeReservation', 'manager.modules.rackBuilder.fields.negativeReservation', 'Negativ reservasjon', filterState.negativeReservationAllowed, { negativeReservationAllowed: 'all' });
        tri('sharedBin', 'manager.modules.rackBuilder.fields.sharedBin', 'Delt lokasjon', filterState.sharedBinYesNo, { sharedBinYesNo: 'all' });
        tri('preferEmptyBin', 'manager.modules.rackBuilder.fields.preferEmptyBin', 'Foretrekk tom lokasjon', filterState.sharedBinPreferEmptyBin, { sharedBinPreferEmptyBin: 'all' });
        if (filterState.itemNo !== 'all') {
            add('itemNo', `${t('warehouse.general.item', {}, 'Vare')}: ${filterState.itemNo === 'has'
                ? t('manager.modules.rackBuilder.filter.hasItem', {}, 'Har vare')
                : t('manager.modules.rackBuilder.filter.empty', {}, 'Tom')}`, { itemNo: 'all' });
        }
        if (filterState.replenishMinLevelFrom > 0 || filterState.replenishMinLevelTo < 9999) {
            add('replenishMin', t('manager.modules.rackBuilder.fields.minReplenishLevel', {}, 'Min pafyllniva'), { replenishMinLevelFrom: 0, replenishMinLevelTo: 9999 });
        }
        if (filterState.replenishMaxLevelFrom > 0 || filterState.replenishMaxLevelTo < 9999) {
            add('replenishMax', t('manager.modules.rackBuilder.fields.maxReplenishLevel', {}, 'Maks pafyllniva'), { replenishMaxLevelFrom: 0, replenishMaxLevelTo: 9999 });
        }
        if (filterState.customSortIDFrom > 0 || filterState.customSortIDTo < 9999) {
            add('customSortId', t('manager.modules.rackBuilder.fields.customSortId', {}, 'Sortering ID'), { customSortIDFrom: 0, customSortIDTo: 9999 });
        }
        if (filterState.sharedBinMaxQuantityFrom > 0 || filterState.sharedBinMaxQuantityTo < 9999) {
            add('sharedBinMaxQty', t('manager.modules.rackBuilder.fields.sharedBinMaxQuantity', {}, 'Maks antall delt'), { sharedBinMaxQuantityFrom: 0, sharedBinMaxQuantityTo: 9999 });
        }
        return chips;
    }, [filterState, t]);

    // Referansedata for lesbare beskrivelser (deles med tooltip/detaljpanel/legend)
    const refData = useBinReferenceData(companyNo);

    // Saldo per lokasjon — lastes først når innholds-/fyllgradslaget eller helsesjekken aktiveres
    const needsContents = colorMode === 'content' || colorMode === 'fillRate' || showHealthCheck;
    const binContents = useBinContents(needsContents, warehouseID);

    /** Kategorisk fargekart: distinkte verdier i lastede reoler → stabil palettfarge */
    const zoneColorMap = useMemo(() => {
        const values = Array.from(new Set(allLocations.map(l => l.stcZoneID).filter((v): v is string => !!v)))
            .sort((a, b) => compareSegments(a, b));
        return new Map(values.map((v, i) => [v, colorForCategoryIndex(i)]));
    }, [allLocations]);

    const binTypeColorMap = useMemo(() => {
        const values = Array.from(new Set(allLocations.map(l => l.binTypeNo))).sort((a, b) => a - b);
        return new Map(values.map((v, i) => [v, colorForCategoryIndex(i)]));
    }, [allLocations]);

    const pickPriorityColorMap = useMemo(() => {
        const values = Array.from(new Set(
            allLocations.map(l => l.pickPriorityNo).filter((v): v is number => v !== null && v !== undefined)
        )).sort((a, b) => a - b);
        return new Map(values.map((v, i) => [v, colorForCategoryIndex(i)]));
    }, [allLocations]);

    /** Farge for en lokasjon i aktivt lag (null = standard styling) */
    const cellColor = useCallback((location: BinLocation): CellColor | null => {
        switch (colorMode) {
            case 'zone':
                return location.stcZoneID ? (zoneColorMap.get(location.stcZoneID) ?? null) : CONTENT_EMPTY;
            case 'binType':
                return binTypeColorMap.get(location.binTypeNo) ?? null;
            case 'pickPriority':
                return (location.pickPriorityNo !== null && location.pickPriorityNo !== undefined)
                    ? (pickPriorityColorMap.get(location.pickPriorityNo) ?? null)
                    : CONTENT_EMPTY;
            case 'content': {
                if (!binContents.contents) return null;
                const summary = binContents.contents.get(location.binCode);
                return summary && summary.totalQuantity > 0 ? CONTENT_OCCUPIED : CONTENT_EMPTY;
            }
            case 'fillRate': {
                if (!binContents.contents) return null;
                const summary = binContents.contents.get(location.binCode);
                if (!summary || summary.totalQuantity <= 0) return CONTENT_EMPTY;
                if (!summary.maxQuantity || summary.maxQuantity <= 0) return FILL_UNKNOWN;
                return colorForFillRate((summary.totalQuantity / summary.maxQuantity) * 100);
            }
            default:
                return null;
        }
    }, [colorMode, zoneColorMap, binTypeColorMap, pickPriorityColorMap, binContents.contents]);

    /** Ekstra celleinnhold for saldo-lagene: antall (og fyllgrad i %) */
    const cellExtra = useCallback((group: BinLocationGroup): React.ReactNode => {
        if (!needsContents || !binContents.contents) return null;
        // 4D-celler: summer over alle skuffer i cellen
        let qty = 0;
        let max = 0;
        let hasMax = true;
        for (const loc of group.locations) {
            const summary = binContents.contents.get(loc.binCode);
            if (summary) {
                qty += summary.totalQuantity;
                if (summary.maxQuantity && summary.maxQuantity > 0) max += summary.maxQuantity;
                else hasMax = false;
            } else {
                hasMax = false;
            }
        }
        if (qty <= 0) return null;
        if (colorMode === 'fillRate') {
            return hasMax && max > 0 ? `${Math.round((qty / max) * 100)} %` : `${qty}`;
        }
        return `${qty}`;
    }, [needsContents, binContents.contents, colorMode]);

    /** Dynamisk legend for aktivt farge-lag */
    const layerLegend = useMemo((): LegendItem[] => {
        switch (colorMode) {
            case 'zone':
                return Array.from(zoneColorMap.entries()).map(([value, color]) => ({
                    key: value, label: refData.describe.zone(value), color
                }));
            case 'binType':
                return Array.from(binTypeColorMap.entries()).map(([value, color]) => ({
                    key: String(value), label: refData.describe.binType(value), color
                }));
            case 'pickPriority':
                return Array.from(pickPriorityColorMap.entries()).map(([value, color]) => ({
                    key: String(value), label: refData.describe.priority(value), color
                }));
            case 'content':
                return [
                    { key: 'empty', label: t('manager.rackGrid.legend.empty', {}, 'Tom'), color: CONTENT_EMPTY },
                    { key: 'occupied', label: t('manager.rackGrid.legend.occupied', {}, 'Har saldo'), color: CONTENT_OCCUPIED }
                ];
            case 'fillRate':
                return [
                    { key: 'empty', label: t('manager.rackGrid.legend.empty', {}, 'Tom'), color: CONTENT_EMPTY },
                    { key: 'lt50', label: '< 50 %', color: colorForFillRate(25) },
                    { key: '50-80', label: '50–80 %', color: colorForFillRate(65) },
                    { key: '80-100', label: '80–100 %', color: colorForFillRate(90) },
                    { key: 'gt100', label: '> 100 %', color: colorForFillRate(110) },
                    { key: 'unknown', label: t('manager.rackGrid.legend.unknownCapacity', {}, 'Ukjent kapasitet'), color: FILL_UNKNOWN }
                ];
            default:
                return [];
        }
    }, [colorMode, zoneColorMap, binTypeColorMap, pickPriorityColorMap, refData.describe, t]);

    /** Søketreff blant synlige (filtrerte) lokasjoner */
    const searchMatches = useMemo(() => {
        const query = searchQuery.trim().toUpperCase();
        if (!query) return [];
        return filteredBinCodes.filter(code => code.toUpperCase().includes(query));
    }, [searchQuery, filteredBinCodes]);

    const searchHighlightSet = useMemo(() => new Set(searchMatches), [searchMatches]);

    /** Enter i søkefeltet: hopp til neste treff (sykler) */
    const handleSearchJump = () => {
        if (searchMatches.length === 0) return;
        const index = searchMatchIndex % searchMatches.length;
        setScrollTarget(searchMatches[index]);
        setSearchMatchIndex(index + 1);
    };

    /** Valgte lokasjoner med fulle data — til masseendrings-forhåndsvisning, angre og etiketter */
    const selectedLocations = useMemo(() => {
        const set = new Set(selectedBinCodes);
        return allLocations.filter(l => set.has(l.binCode));
    }, [allLocations, selectedBinCodes]);

    /** Etikett-elementer for LabelPrintModal (delt utskriftsdialog med skrivervalg/preview/progresjon) */
    const labelItems = useMemo((): LabelPrintItem[] =>
        selectedLocations.map(loc => ({
            id: loc.binCode,
            displayName: loc.binCode,
            binCode: loc.binCode,
            locationName: loc.binCode,
            warehouseId: warehouseID,
            zoneName: loc.stcZoneID,
            description: loc.description1
        })), [selectedLocations, warehouseID]);

    /**
     * Prefill for utvid/kopier fra valgt reols struktur: dimensjoner fra aksene,
     * attributter fra første lokasjon, skuffantall fra største celle.
     */
    const buildPrefillFromRack = (rackNumber: string): RackDialogPrefill | null => {
        const structure = rackStructures.get(rackNumber);
        if (!structure || structure.locations.length === 0) return null;

        const first = structure.locations[0];
        const cellCounts = new Map<string, number>();
        for (const loc of structure.locations) {
            const key = `${loc.vertical}-${loc.shelf}`;
            cellCounts.set(key, (cellCounts.get(key) ?? 0) + 1);
        }
        const maxDrawers = Math.max(...Array.from(cellCounts.values()));

        return {
            rackNumber,
            verticalCount: structure.verticals.length,
            shelfCount: structure.shelves.length,
            drawerCount: maxDrawers > 1 ? maxDrawers : 0,
            stcZoneID: first.stcZoneID ?? '001',
            binSizeNo: first.binSizeNo,
            binTypeNo: first.binTypeNo,
            resourceNo: first.resourceNo,
            binGrpNo: first.binGrpNo,
            pickPriorityNo: first.pickPriorityNo,
            pickableYesNo: first.pickableYesNo ?? true,
            reservationAllowed: first.reservationAllowed ?? true,
            autoReplenishment: first.autoReplenishment ?? false
        };
    };

    /** Reoler i chips-raden, filtrert på reol-søket (for lagre med svært mange reoler) */
    const visibleRacks = useMemo(() => {
        const query = rackSearch.trim().toUpperCase();
        if (!query) return racks;
        return racks.filter(r => r.rackNumber.toUpperCase().includes(query));
    }, [racks, rackSearch]);

    const singleSelectedRack = selectedRackNumbers.length === 1 ? selectedRackNumbers[0] : null;
    const canExtendOrCopy = singleSelectedRack !== null && rackStructures.has(singleSelectedRack);

    const openExtendDialog = () => {
        if (!singleSelectedRack) return;
        const prefill = buildPrefillFromRack(singleSelectedRack);
        if (prefill) setRackDialog({ mode: 'extend', prefill });
    };

    const openCopyDialog = () => {
        if (!singleSelectedRack) return;
        const prefill = buildPrefillFromRack(singleSelectedRack);
        if (prefill) setRackDialog({ mode: 'copy', prefill: { ...prefill, rackNumber: undefined } });
    };

    /** Angre siste masseendring: grupper bins med identiske før-verdier → ett bulk-kall per gruppe */
    const handleUndoLastBulk = async () => {
        if (!lastBulkUndo || isUndoing) return;

        const confirmed = window.confirm(
            t('manager.modules.rackBuilder.bulk.undoConfirm', { '0': lastBulkUndo.entries.length },
                'Tilbakestill {0} lokasjon(er) til verdiene før siste masseendring?')
        );
        if (!confirmed) return;

        setIsUndoing(true);
        setBulkNotice(null);
        try {
            const groups = new Map<string, { before: BulkUndoData['entries'][number]['before']; binCodes: string[] }>();
            for (const entry of lastBulkUndo.entries) {
                const key = JSON.stringify(entry.before, Object.keys(entry.before).sort());
                const group = groups.get(key);
                if (group) group.binCodes.push(entry.binCode);
                else groups.set(key, { before: entry.before, binCodes: [entry.binCode] });
            }

            let successCount = 0;
            const errors: string[] = [];
            for (const group of Array.from(groups.values())) {
                try {
                    const response = await stcBinService.bulkUpdateBins({
                        companyNo,
                        warehouseID: lastBulkUndo.warehouseID,
                        binCodes: group.binCodes,
                        ...group.before
                    });
                    successCount += response.successCount;
                    errors.push(...response.errors);
                } catch (err) {
                    errors.push(err instanceof Error ? err.message : String(err));
                }
            }

            if (errors.length > 0) {
                setBulkNotice({
                    type: 'warning',
                    text: t('manager.modules.rackBuilder.bulk.undoPartial', { '0': successCount, '1': errors.length },
                        'Tilbakestilte {0} lokasjon(er) — {1} feil oppstod.')
                });
            } else {
                setBulkNotice({
                    type: 'success',
                    text: t('manager.modules.rackBuilder.bulk.undoSuccess', { '0': successCount },
                        '{0} lokasjon(er) tilbakestilt til før-verdiene.')
                });
            }

            setLastBulkUndo(null);
            setSelectedBinCodes([]);
            setDetailGroup(null);
            loadLocationsForSelectedRacks();
        } finally {
            setIsUndoing(false);
        }
    };

    // Load racks on mount
    useEffect(() => {
        loadRacks();
        loadFilterOptions();
    }, [companyNo, warehouseID]);

    // Deep-link fra Lokasjon HeatMap: ?module=rack-builder&rack=A&bin=A1307
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const rackParam = params.get('rack');
        const binParam = params.get('bin');
        if (rackParam) {
            setSelectedRackNumbers([rackParam]);
        }
        if (binParam) {
            setSearchQuery(binParam);
            pendingDeepLinkBin.current = binParam;
        }
    }, []);

    // Scroll til deep-link-bin når lokasjonene er lastet
    useEffect(() => {
        const target = pendingDeepLinkBin.current;
        if (target && filteredBinCodes.includes(target)) {
            setScrollTarget(target);
            pendingDeepLinkBin.current = null;
        }
        // filteredBinCodes er avledet state — memoisert i denne komponenten
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredLocations]);

    /**
     * Load all racks for warehouse (FR-001)
     */
    const loadRacks = async () => {
        if (!companyNo) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
            const data = await stcBinService.getRackSummary(companyNo, warehouseID);
            setRacks(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load racks');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Load filter options (FR-005)
     */
    const loadFilterOptions = async () => {
        if (!companyNo) return;
        
        try {
            const options = await stcBinService.getFilterOptions(companyNo, warehouseID);
            setFilterOptions(options);
        } catch (err) {
            console.error('Failed to load filter options:', err);
        }
    };

    /**
     * Load locations for selected racks (FR-002)
     */
    const loadLocationsForSelectedRacks = async () => {
        if (selectedRackNumbers.length === 0) {
            setAllLocations([]);
            setFilteredLocations([]);
            setRackStructures(new Map());
            return;
        }

        setIsLoading(true);
        setError(null);
        
        try {
            const results = await Promise.all(
                selectedRackNumbers.map(async rackNumber => ({
                    rackNumber,
                    structure: await stcBinService.getRackStructure(companyNo, warehouseID, rackNumber)
                }))
            );

            const allLocs: BinLocation[] = [];
            const structures = new Map<string, RackStructure>();
            for (const { rackNumber, structure } of results) {
                structures.set(rackNumber, structure);
                allLocs.push(...structure.locations);
            }

            setRackStructures(structures);
            setAllLocations(allLocs);
            // Apply current filter
            await applyFilter(allLocs);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load locations');
        } finally {
            setIsLoading(false);
        }
    };

    // Reload locations when selected racks change
    useEffect(() => {
        loadLocationsForSelectedRacks();
        // Clear selection when racks change
        setSelectedBinCodes([]);
        setDetailGroup(null);
    }, [selectedRackNumbers]);

    // Nullstill treff-syklus når søket endres
    useEffect(() => {
        setSearchMatchIndex(0);
        setScrollTarget(null);
    }, [searchQuery]);

    /**
     * Apply current filter (FR-004) — tverrfilter: kjøres mot HELE lageret (ingen rackNumbers)
     * slik at reol-chipsene kan vise treff per reol; griden viser treffene i lastede reoler.
     * Uten aktive filtre gjøres ingen API-kall — alle lastede lokasjoner vises.
     */
    const applyFilter = async (locations = allLocations) => {
        if (!hasActiveFilters) {
            setFilteredLocations(locations);
            setRackHitCounts(null);
            setFilterError(false);
            return;
        }

        try {
            const request = {
                companyNo,
                warehouseID,
                binSizeNo: filterState.binSizeNo.length > 0 ? filterState.binSizeNo : undefined,
                binTypeNo: filterState.binTypeNo.length > 0 ? filterState.binTypeNo : undefined,
                resourceNo: filterState.resourceNo.length > 0 ? filterState.resourceNo : undefined,
                stcZoneID: filterState.stcZoneID.length > 0 ? filterState.stcZoneID : undefined,
                binGrpNo: filterState.binGrpNo.length > 0 ? filterState.binGrpNo : undefined,
                stcBinCatNo: filterState.stcBinCatNo.length > 0 ? filterState.stcBinCatNo : undefined,
                pickPriorityNo: filterState.pickPriorityNo.length > 0 ? filterState.pickPriorityNo : undefined,
                pickableYesNo: filterState.pickableYesNo === 'yes' ? true : filterState.pickableYesNo === 'no' ? false : undefined,
                hasItemNo: filterState.itemNo === 'has' ? true : filterState.itemNo === 'empty' ? false : undefined,
                // Extended filter properties
                putAwayPriorityNo: filterState.putAwayPriorityNo.length > 0 ? filterState.putAwayPriorityNo : undefined,
                reservationAllowed: filterState.reservationAllowed === 'yes' ? true : filterState.reservationAllowed === 'no' ? false : undefined,
                autoReplenishment: filterState.autoReplenishment === 'yes' ? true : filterState.autoReplenishment === 'no' ? false : undefined,
                negativeReservationAllowed: filterState.negativeReservationAllowed === 'yes' ? true : filterState.negativeReservationAllowed === 'no' ? false : undefined,
                sharedBinYesNo: filterState.sharedBinYesNo === 'yes' ? true : filterState.sharedBinYesNo === 'no' ? false : undefined,
                sharedBinPreferEmptyBin: filterState.sharedBinPreferEmptyBin === 'yes' ? true : filterState.sharedBinPreferEmptyBin === 'no' ? false : undefined,
                replenishMinLevelFrom: filterState.replenishMinLevelFrom > 0 ? filterState.replenishMinLevelFrom : undefined,
                replenishMinLevelTo: filterState.replenishMinLevelTo < 9999 ? filterState.replenishMinLevelTo : undefined,
                replenishMaxLevelFrom: filterState.replenishMaxLevelFrom > 0 ? filterState.replenishMaxLevelFrom : undefined,
                replenishMaxLevelTo: filterState.replenishMaxLevelTo < 9999 ? filterState.replenishMaxLevelTo : undefined,
                customSortIDFrom: filterState.customSortIDFrom > 0 ? filterState.customSortIDFrom : undefined,
                customSortIDTo: filterState.customSortIDTo < 9999 ? filterState.customSortIDTo : undefined,
                sharedBinMaxQuantityFrom: filterState.sharedBinMaxQuantityFrom > 0 ? filterState.sharedBinMaxQuantityFrom : undefined,
                sharedBinMaxQuantityTo: filterState.sharedBinMaxQuantityTo < 9999 ? filterState.sharedBinMaxQuantityTo : undefined
            };

            const matches = await stcBinService.filterBins(request);

            // Treff per reol i hele lageret → badges på reol-chipsene
            const hits = new Map<string, number>();
            for (const match of matches) {
                if (!match.rackNumber) continue;
                hits.set(match.rackNumber, (hits.get(match.rackNumber) ?? 0) + 1);
            }
            setRackHitCounts(hits);

            // Griden viser kun treffene som ligger i de lastede reolene
            const loadedBinCodes = new Set(locations.map(l => l.binCode));
            setFilteredLocations(matches.filter(m => loadedBinCodes.has(m.binCode)));
            setFilterError(false);
        } catch (err) {
            console.error('Failed to filter bins:', err);
            // Ærlig fallback: vis ufiltrert, men si ifra — brukeren kan ellers masseendre feil bins
            setFilteredLocations(locations);
            setRackHitCounts(null);
            setFilterError(true);
        }
    };

    /**
     * Handle rack selection — chips-modell: klikk toggler valget direkte (ingen Ctrl nødvendig)
     */
    const handleRackSelect = (rackNumber: string) => {
        setSelectedRackNumbers(prev =>
            prev.includes(rackNumber)
                ? prev.filter(r => r !== rackNumber)
                : [...prev, rackNumber]
        );
    };

    /**
     * Handle location selection in grid
     */
    const handleLocationSelect = (binCode: string, isCtrlKey: boolean, isShiftKey: boolean) => {
        if (isShiftKey && selectedBinCodes.length > 0) {
            // Range select
            const lastSelected = selectedBinCodes[selectedBinCodes.length - 1];
            const lastIndex = filteredLocations.findIndex(l => l.binCode === lastSelected);
            const currentIndex = filteredLocations.findIndex(l => l.binCode === binCode);
            
            if (lastIndex !== -1 && currentIndex !== -1) {
                const start = Math.min(lastIndex, currentIndex);
                const end = Math.max(lastIndex, currentIndex);
                const range = filteredLocations.slice(start, end + 1).map(l => l.binCode);
                setSelectedBinCodes(prev => Array.from(new Set([...prev, ...range])));
            }
        } else if (isCtrlKey) {
            // Multi-select
            setSelectedBinCodes(prev =>
                prev.includes(binCode)
                    ? prev.filter(b => b !== binCode)
                    : [...prev, binCode]
            );
        } else {
            // Single select
            setSelectedBinCodes([binCode]);
        }
    };

    /**
     * Select all visible locations
     */
    const handleSelectAll = () => {
        setSelectedBinCodes(filteredLocations.map(l => l.binCode));
    };

    /**
     * Clear all selections
     */
    const handleClearSelection = () => {
        setSelectedBinCodes([]);
    };

    /**
     * Reset all filters to default values
     */
    const handleResetFilters = () => {
        setFilterState({
            binSizeNo: [],
            binTypeNo: [],
            resourceNo: [],
            stcZoneID: [],
            binGrpNo: [],
            stcBinCatNo: [],
            pickPriorityNo: [],
            pickableYesNo: 'all',
            itemNo: 'all',
            // Extended filter state
            putAwayPriorityNo: [],
            reservationAllowed: 'all',
            autoReplenishment: 'all',
            negativeReservationAllowed: 'all',
            sharedBinYesNo: 'all',
            sharedBinPreferEmptyBin: 'all',
            replenishMinLevelFrom: 0,
            replenishMinLevelTo: 9999,
            replenishMaxLevelFrom: 0,
            replenishMaxLevelTo: 9999,
            customSortIDFrom: 0,
            customSortIDTo: 9999,
            sharedBinMaxQuantityFrom: 0,
            sharedBinMaxQuantityTo: 9999
        });
    };

    // Reapply filter when filter state changes (debounced: multi-select-filtre klikkes raskt etter hverandre).
    // Kjøres også uten lastede reoler — tverrfilteret gir treff-badges på reol-chipsene uansett.
    useEffect(() => {
        const handle = setTimeout(() => {
            applyFilter();
        }, 300);
        return () => clearTimeout(handle);
    }, [filterState]);

    return (
        <div className="flex flex-col h-screen w-full overflow-hidden bg-gray-50">
            {/* Toolbar - Compact */}
            <div className="flex items-center justify-between bg-white px-4 py-2 border-b border-gray-200 shadow-sm flex-shrink-0">
                <div className="flex items-center space-x-3">
                    <BackButton onClick={onBack || (() => {})} />
                    <Grid3x3 className="w-5 h-5 text-blue-600" />
                    <div>
                        <h1 className="text-lg font-bold text-gray-800">{t('manager.modules.rackBuilder.app.title', {}, 'Reolbygger')}</h1>
                        <p className="text-xs text-gray-600">
                            {t('manager.modules.rackBuilder.app.warehouseCompany', { '0': warehouseID, '1': companyNo }, 'Lager: {0} | Selskap: {1}')}
                        </p>
                    </div>
                </div>
                
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => setRackDialog({ mode: 'create' })}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                        <span>{t('manager.modules.rackBuilder.app.newRack', {}, 'Ny Reol')}</span>
                    </button>

                    <button
                        onClick={openExtendDialog}
                        disabled={!canExtendOrCopy}
                        title={!canExtendOrCopy ? t('manager.modules.rackBuilder.app.selectOneRackHint', {}, 'Velg nøyaktig én reol først') : undefined}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Expand className="w-5 h-5" />
                        <span>{t('manager.modules.rackBuilder.app.extendRack', {}, 'Utvid reol')}</span>
                    </button>

                    <button
                        onClick={openCopyDialog}
                        disabled={!canExtendOrCopy}
                        title={!canExtendOrCopy ? t('manager.modules.rackBuilder.app.selectOneRackHint', {}, 'Velg nøyaktig én reol først') : undefined}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Copy className="w-5 h-5" />
                        <span>{t('manager.modules.rackBuilder.app.copyRack', {}, 'Kopier reol')}</span>
                    </button>

                    <button
                        onClick={loadRacks}
                        disabled={isLoading}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                        <span>{t('common.buttons.refresh', {}, 'Oppdater')}</span>
                    </button>
                </div>
            </div>

            {/* Error message */}
            {error && (
                <div className="bg-red-50 border-b border-red-200 text-red-800 px-4 py-2 text-sm flex-shrink-0">
                    {error}
                </div>
            )}

            {/* Filter error - filter API failed, showing unfiltered fallback */}
            {filterError && (
                <div className="bg-red-50 border-b border-red-200 text-red-800 px-4 py-2 text-sm flex-shrink-0">
                    {t('manager.modules.rackBuilder.app.filterError', {}, 'Filtrering feilet — viser ufiltrert liste. Prøv igjen eller nullstill filtrene.')}
                </div>
            )}

            {/* Resultat fra angre masseendring */}
            {bulkNotice && (
                <div className={`border-b px-4 py-2 text-sm flex-shrink-0 flex items-center justify-between ${
                    bulkNotice.type === 'success'
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                }`}>
                    <span>{bulkNotice.text}</span>
                    <button
                        onClick={() => setBulkNotice(null)}
                        className="font-semibold text-xs hover:underline"
                    >
                        {t('common.buttons.close', {}, 'Lukk')}
                    </button>
                </div>
            )}

            {/* Reolvelger — kompakt chips-rad: klikk toggler valg, detaljer i tooltip.
                Treff-badges vises per reol når tverrfilteret er aktivt. */}
            <div className="bg-white border-b border-gray-200 flex-shrink-0 px-4 py-1.5">
                {isLoading && racks.length === 0 ? (
                    <div className="flex items-center py-1">
                        <Loader className="w-4 h-4 animate-spin text-blue-600" />
                        <span className="ml-2 text-sm text-gray-600">{t('manager.modules.rackBuilder.app.loadingRacks', {}, 'Laster reoler...')}</span>
                    </div>
                ) : racks.length === 0 ? (
                    <div className="py-1 text-sm text-gray-500">
                        {t('manager.modules.rackBuilder.app.noRacks', {}, 'Ingen reoler funnet. Opprett en ny reol for å komme i gang.')}
                    </div>
                ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                            {t('manager.modules.rackBuilder.app.selectRacks', {}, 'Velg Reoler')}
                        </h2>
                        {selectedRackNumbers.length > 0 && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full whitespace-nowrap">
                                {t('manager.modules.rackBuilder.app.selectedCount', { '0': selectedRackNumbers.length }, '{0} valgt')}
                            </span>
                        )}

                        {/* Reol-søk — for lagre med mange reoler */}
                        {racks.length > 15 && (
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                                <input
                                    type="text"
                                    value={rackSearch}
                                    onChange={(e) => setRackSearch(e.target.value)}
                                    placeholder={t('manager.modules.rackBuilder.app.rackSearchPlaceholder', {}, 'Søk reol...')}
                                    className="w-28 pl-6 pr-5 py-0.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                />
                                {rackSearch && (
                                    <button
                                        onClick={() => setRackSearch('')}
                                        className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Chips */}
                        <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0">
                            {visibleRacks.map(rack => {
                                const isSelected = selectedRackNumbers.includes(rack.rackNumber);
                                const hits = rackHitCounts?.get(rack.rackNumber) ?? 0;
                                const filterActive = rackHitCounts !== null;
                                return (
                                    <button
                                        key={rack.rackNumber}
                                        onClick={() => handleRackSelect(rack.rackNumber)}
                                        title={`${t('manager.modules.rackBuilder.app.locationCountShort', { '0': rack.locationCount }, '{0} lok')} · V${rack.minVertical}-${rack.maxVertical} × H${rack.minShelf}-${rack.maxShelf}${
                                            filterActive ? ` · ${t('manager.modules.rackBuilder.app.hitsTooltip', { '0': hits }, '{0} treff i filteret')}` : ''
                                        }`}
                                        className={`flex items-center gap-1 px-2 py-0.5 text-xs rounded border transition-colors whitespace-nowrap ${
                                            isSelected
                                                ? 'bg-blue-100 border-blue-500 text-blue-800 font-semibold'
                                                : filterActive && hits === 0
                                                    ? 'bg-white border-gray-200 text-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                                        }`}
                                    >
                                        <span>{t('manager.modules.rackBuilder.app.rackLabel', { '0': rack.rackNumber }, 'Reol {0}')}</span>
                                        {filterActive && hits > 0 && (
                                            <span className={`px-1 rounded-full text-[10px] font-bold ${
                                                isSelected ? 'bg-blue-600 text-white' : 'bg-amber-100 text-amber-800 border border-amber-300'
                                            }`}>
                                                {hits}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                            {visibleRacks.length === 0 && (
                                <span className="text-xs text-gray-500 italic">
                                    {t('manager.modules.rackBuilder.filter.noMatch', {}, 'Ingen treff')}
                                </span>
                            )}
                        </div>

                        {selectedRackNumbers.length > 0 && (
                            <button
                                onClick={() => setSelectedRackNumbers([])}
                                className="text-xs text-gray-600 hover:text-gray-800 underline whitespace-nowrap"
                            >
                                {t('manager.modules.rackBuilder.app.clearAll', {}, 'Fjern alle')}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Main content area - 2 columns */}
            <div className="flex-1 min-h-0 flex gap-2 p-2">
                    {/* Filter panel (Left) — kollapsbar, lukket som standard */}
                    {showFilterPanel && (
                    <div className="w-64 bg-white rounded-lg shadow flex flex-col flex-shrink-0">
                        <div className="bg-white border-b border-gray-200 px-3 py-2 flex-shrink-0 flex items-center justify-between">
                            <h3 className="font-semibold text-sm text-gray-800">{t('common.buttons.filter', {}, 'Filtrer')}</h3>
                            <button
                                onClick={() => setShowFilterPanel(false)}
                                className="p-0.5 text-gray-500 hover:text-gray-800 rounded"
                                title={t('common.buttons.close', {}, 'Lukk')}
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-3">
                            <FilterPanel
                                filterOptions={filterOptions}
                                filterState={filterState}
                                onFilterChange={setFilterState}
                                onReset={handleResetFilters}
                            />
                        </div>
                    </div>
                    )}

                    {/* Grid view (Center) - DOMINANT ELEMENT that fills space */}
                    <div className="flex-1 bg-white rounded-lg shadow flex flex-col min-w-0">
                        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 flex-shrink-0">
                            <h3 className="font-semibold text-sm text-gray-800">
                                {t('manager.modules.rackBuilder.app.locations', {}, 'Lokasjoner')} {filteredLocations.length > 0 && `(${filteredLocations.length})`}
                            </h3>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={handleSelectAll}
                                    disabled={filteredLocations.length === 0}
                                    className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors disabled:opacity-50"
                                >
                                    {t('manager.modules.rackBuilder.app.selectAll', {}, 'Velg alle')}
                                </button>
                                <button
                                    onClick={handleClearSelection}
                                    disabled={selectedBinCodes.length === 0}
                                    className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                                >
                                    {t('manager.modules.rackBuilder.app.clearSelection', {}, 'Fjern valg')}
                                </button>
                                <button
                                    onClick={() => setShowLabelModal(true)}
                                    disabled={selectedBinCodes.length === 0}
                                    title={selectedBinCodes.length === 0 ? t('manager.modules.rackBuilder.app.selectLocationsFirst', {}, 'Velg lokasjoner først') : undefined}
                                    className="flex items-center space-x-2 px-3 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Printer className="w-4 h-4" />
                                    <span>{t('manager.modules.rackBuilder.app.printLabels', {}, 'Skriv etiketter')}</span>
                                </button>
                                {lastBulkUndo && (
                                    <button
                                        onClick={handleUndoLastBulk}
                                        disabled={isUndoing}
                                        title={t('manager.modules.rackBuilder.bulk.undoTooltip', { '0': lastBulkUndo.entries.length }, 'Tilbakestill {0} lokasjon(er) til verdiene før siste masseendring')}
                                        className="flex items-center space-x-2 px-3 py-1 text-xs bg-orange-100 text-orange-800 border border-orange-300 rounded hover:bg-orange-200 transition-colors disabled:opacity-50"
                                    >
                                        {isUndoing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                                        <span>{t('manager.modules.rackBuilder.bulk.undoButton', {}, 'Angre siste masseendring')}</span>
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowBulkUpdateModal(true)}
                                    disabled={selectedBinCodes.length === 0}
                                    className="flex items-center space-x-2 px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Settings className="w-4 h-4" />
                                    <span>{t('manager.modules.rackBuilder.app.maintainLocations', {}, 'Vedlikehold lokasjoner')}</span>
                                </button>
                            </div>
                        </div>

                        {/* Verktøylinje: filter, farge-lag, søk, tetthet */}
                        <div className="flex items-center gap-3 px-4 py-1.5 border-b border-gray-200 flex-shrink-0 flex-wrap">
                            {/* Filter (åpner/lukker venstrekolonnen) */}
                            <button
                                onClick={() => setShowFilterPanel(prev => !prev)}
                                className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded border transition-colors ${
                                    showFilterPanel || hasActiveFilters
                                        ? 'bg-blue-100 border-blue-500 text-blue-800 font-semibold'
                                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                                }`}
                            >
                                <SlidersHorizontal className="w-3.5 h-3.5" />
                                {t('common.buttons.filter', {}, 'Filtrer')}
                                {activeFilterChips.length > 0 && (
                                    <span className="px-1 rounded-full text-[10px] font-bold bg-blue-600 text-white">
                                        {activeFilterChips.length}
                                    </span>
                                )}
                            </button>

                            {/* Farg etter */}
                            <div className="flex items-center gap-1.5">
                                <label className="text-xs text-gray-600 whitespace-nowrap">
                                    {t('manager.rackGrid.toolbar.colorBy', {}, 'Farg etter')}
                                </label>
                                <select
                                    value={colorMode}
                                    onChange={(e) => setColorMode(e.target.value as ColorMode)}
                                    className="text-xs border border-gray-300 rounded px-2 py-1 bg-white text-gray-800 focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value="none">{t('manager.rackGrid.toolbar.colorNone', {}, 'Standard')}</option>
                                    <option value="content">{t('manager.rackGrid.toolbar.colorContent', {}, 'Innhold (tom/opptatt)')}</option>
                                    <option value="fillRate">{t('manager.rackGrid.toolbar.colorFillRate', {}, 'Fyllgrad')}</option>
                                    <option value="zone">{t('warehouse.general.zone', {}, 'Sone')}</option>
                                    <option value="binType">{t('manager.modules.rackBuilder.fields.binType', {}, 'Bin Type')}</option>
                                    <option value="pickPriority">{t('manager.modules.rackBuilder.fields.pickPriority', {}, 'Plukk Prioritet')}</option>
                                </select>
                                {needsContents && binContents.isLoading && (
                                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                                )}
                            </div>

                            {/* Søk / hopp til lokasjon */}
                            <div className="flex items-center gap-1.5">
                                <div className="relative">
                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSearchJump(); }}
                                        placeholder={t('manager.rackGrid.toolbar.searchPlaceholder', {}, 'Søk lokasjon...')}
                                        className="w-44 pl-7 pr-6 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    {searchQuery && (
                                        <button
                                            onClick={() => setSearchQuery('')}
                                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                                {searchQuery && (
                                    <button
                                        onClick={handleSearchJump}
                                        disabled={searchMatches.length === 0}
                                        className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50 whitespace-nowrap"
                                        title={t('manager.rackGrid.toolbar.jumpToNext', {}, 'Hopp til neste treff (Enter)')}
                                    >
                                        {t('manager.rackGrid.toolbar.matchCount', { '0': searchMatches.length }, '{0} treff')}
                                    </button>
                                )}
                            </div>

                            {/* Helsesjekk */}
                            <button
                                onClick={() => setShowHealthCheck(prev => !prev)}
                                className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded border transition-colors ${
                                    showHealthCheck
                                        ? 'bg-yellow-100 border-yellow-400 text-yellow-800 font-semibold'
                                        : 'bg-white border-gray-300 text-gray-700 hover:border-yellow-400'
                                }`}
                            >
                                <Stethoscope className="w-3.5 h-3.5" />
                                {t('manager.modules.rackBuilder.health.button', {}, 'Helsesjekk')}
                            </button>

                            {/* Tetthet S/M/L */}
                            <div className="flex items-center gap-1 ml-auto">
                                <span className="text-xs text-gray-600">{t('manager.rackGrid.toolbar.density', {}, 'Tetthet')}</span>
                                {(['S', 'M', 'L'] as GridDensity[]).map(d => (
                                    <button
                                        key={d}
                                        onClick={() => setDensity(d)}
                                        className={`px-2 py-1 text-xs rounded border ${
                                            density === d
                                                ? 'bg-blue-100 border-blue-500 text-blue-800 font-semibold'
                                                : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
                                        }`}
                                    >
                                        {d}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Aktive filtre som chips — synlige uten å åpne filterpanelet */}
                        {activeFilterChips.length > 0 && (
                            <div className="flex items-center gap-1.5 px-4 py-1 border-b border-gray-200 flex-shrink-0 flex-wrap">
                                <span className="text-xs text-gray-600">
                                    {t('manager.modules.rackBuilder.app.activeFilters', {}, 'Aktive filtre:')}
                                </span>
                                {activeFilterChips.map(chip => (
                                    <span
                                        key={chip.key}
                                        className="flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-50 text-blue-800 border border-blue-200 rounded-full"
                                    >
                                        {chip.label}
                                        <button
                                            onClick={chip.onClear}
                                            className="text-blue-600 hover:text-blue-900"
                                            title={t('manager.modules.rackBuilder.app.clearFilter', {}, 'Fjern dette filteret')}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                                <button
                                    onClick={handleResetFilters}
                                    className="text-xs text-gray-600 hover:text-gray-800 underline"
                                >
                                    {t('manager.modules.rackBuilder.filter.resetFilters', {}, 'Nullstill filtre')}
                                </button>
                            </div>
                        )}

                        {/* Dynamisk legend for aktivt farge-lag */}
                        {layerLegend.length > 0 && (
                            <div className="flex items-center gap-3 px-4 py-1 border-b border-gray-200 flex-shrink-0 flex-wrap">
                                {layerLegend.map(item => (
                                    <div key={item.key} className="flex items-center gap-1.5">
                                        <div
                                            className="w-3 h-3 rounded border"
                                            style={{ backgroundColor: item.color.background, borderColor: item.color.border ?? item.color.background }}
                                        />
                                        <span className="text-xs text-gray-700">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Konfigurasjons-helsesjekk */}
                        {showHealthCheck && (
                            <HealthCheckPanel
                                structures={rackStructures}
                                allLocations={allLocations}
                                contents={binContents.contents}
                                isLoadingContents={binContents.isLoading}
                                onSelectBins={(binCodes) => setSelectedBinCodes(binCodes)}
                                onClose={() => setShowHealthCheck(false)}
                            />
                        )}

                        {/* Saldo-lag feilet */}
                        {needsContents && binContents.error && (
                            <div className="bg-red-50 border-b border-red-200 text-red-800 px-4 py-1.5 text-xs flex-shrink-0">
                                {t('manager.rackGrid.contentsError', {}, 'Kunne ikke hente saldo — laget viser ufullstendige data.')} ({binContents.error})
                            </div>
                        )}
                        
                        <div className="flex-1 overflow-hidden p-2">
                            {isLoading ? (
                                <div className="flex items-center justify-center h-full">
                                    <Loader className="w-6 h-6 animate-spin text-blue-600" />
                                    <span className="ml-2 text-sm text-gray-600">{t('manager.modules.rackBuilder.app.loadingLocations', {}, 'Laster lokasjoner...')}</span>
                                </div>
                            ) : selectedRackNumbers.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-sm text-gray-500 gap-1">
                                    <Grid3x3 className="w-10 h-10 text-gray-300" />
                                    <span>{t('manager.modules.rackBuilder.app.selectRackPrompt', {}, 'Velg en eller flere reoler ovenfor for å vise lokasjoner')}</span>
                                    {rackHitCounts !== null && (
                                        <span className="text-xs">
                                            {t('manager.modules.rackBuilder.app.selectRackFilterHint', {}, 'Filteret er aktivt — reoler med treff er markert i raden over')}
                                        </span>
                                    )}
                                </div>
                            ) : filteredLocations.length === 0 ? (
                                <div className="flex items-center justify-center h-full text-sm text-gray-500">
                                    {t('manager.modules.rackBuilder.app.noLocations', {}, 'Ingen lokasjoner funnet')}
                                </div>
                            ) : (
                                <div className="h-full flex gap-2 min-h-0">
                                    <div className="flex-1 space-y-4 overflow-y-auto min-w-0">
                                        {/* Show each selected rack. Én reol: fyll høyden (intern scroll i grid).
                                            Flere reoler: naturlig høyde per reol så alle er synlige, ytre container scroller. */}
                                        {selectedRackNumbers.map(rackNumber => {
                                            const structure = rackStructures.get(rackNumber);
                                            if (!structure) return null;
                                            const isSingleRack = selectedRackNumbers.length === 1;

                                            return (
                                                <div key={rackNumber} className={`flex flex-col min-h-0 ${isSingleRack ? 'h-full' : ''}`}>
                                                    <h4 className={`font-semibold text-sm text-gray-800 mb-2 flex-shrink-0 ${!isSingleRack ? 'sticky top-0 z-20 bg-white py-1' : ''}`}>
                                                        {t('manager.modules.rackBuilder.app.rackLabel', { '0': rackNumber }, 'Reol {0}')}
                                                    </h4>
                                                    <div className={isSingleRack ? 'flex-1 min-h-0' : ''}>
                                                        <RackGridCore
                                                            rackStructure={structure}
                                                            selectedBinCodes={selectedBinCodes}
                                                            onSelectionChange={setSelectedBinCodes}
                                                            filteredBinCodes={filteredBinCodes}
                                                            density={density}
                                                            cellColor={colorMode !== 'none' ? cellColor : undefined}
                                                            cellExtra={needsContents ? cellExtra : undefined}
                                                            highlightedBinCodes={searchHighlightSet.size > 0 ? searchHighlightSet : undefined}
                                                            onLocationClick={setDetailGroup}
                                                            scrollToBinCode={scrollTarget}
                                                            describe={refData.describe}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Klikk-detaljpanel */}
                                    {detailGroup && (
                                        <DetailPanel
                                            group={detailGroup}
                                            onClose={() => setDetailGroup(null)}
                                            describe={refData.describe}
                                            actions={
                                                <button
                                                    onClick={() => window.open(
                                                        `/?module=location-heatmap&rack=${encodeURIComponent(detailGroup.rackNumber)}&bin=${encodeURIComponent(detailGroup.primaryLocation.binCode)}`,
                                                        '_blank'
                                                    )}
                                                    className="flex items-center space-x-1 px-2 py-1 text-[11px] bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                                                >
                                                    <ExternalLink className="w-3 h-3" />
                                                    <span>{t('manager.rackGrid.detail.openInHeatmap', {}, 'Åpne i Lokasjon HeatMap')}</span>
                                                </button>
                                            }
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
            </div>

            {/* Create/Extend/Copy Rack Modal (FR-106) */}
            {rackDialog && (
                <CreateRackDialog
                    warehouseID={warehouseID}
                    mode={rackDialog.mode}
                    prefill={rackDialog.prefill}
                    onClose={() => setRackDialog(null)}
                    onSuccess={() => {
                        loadRacks();
                        loadLocationsForSelectedRacks();
                    }}
                />
            )}

            {/* Etikettutskrift for valgte lokasjoner — delt utskriftsdialog */}
            <LabelPrintModal
                isOpen={showLabelModal}
                onClose={() => setShowLabelModal(false)}
                printType="location"
                items={labelItems}
            />

            {/* Bulk Update Modal */}
            <BulkUpdateModal
                isOpen={showBulkUpdateModal}
                onClose={() => setShowBulkUpdateModal(false)}
                selectedBinCodes={selectedBinCodes}
                warehouseID={warehouseID}
                filterOptions={filterOptions}
                selectedLocations={selectedLocations}
                onUpdateApplied={setLastBulkUndo}
                onUpdateSuccess={() => {
                    loadLocationsForSelectedRacks();
                    setSelectedBinCodes([]);
                    setDetailGroup(null);
                    setShowBulkUpdateModal(false);
                }}
                onDeleteSuccess={() => {
                    loadRacks();
                    loadLocationsForSelectedRacks();
                    setSelectedBinCodes([]);
                    setDetailGroup(null);
                    setShowBulkUpdateModal(false);
                }}
            />
        </div>
    );
};
